<template>
  <v-dialog
    v-model="dialog"
    width="448"
    open-on-focus
    no-click-animation
    persistent
    content-class="swap-entry-dialog__dialog"
    @click:outside="onClickOutside"
    @keydown="onKeydown"
  >
    <v-card elevation="0">
      <v-form
        ref="form"
        class="swap-entry-dialog-form"
        @submit.prevent="onSubmit"
      >
        <div class="swap-entry-dialog-form-body">
          <v-toolbar flat height="76" class="swap-entry-dialog__toolbar">
            <v-toolbar-title class="swap-entry-dialog-toolbar-title"
              >Add entry</v-toolbar-title
            >
            <v-spacer></v-spacer>
            <v-btn color="accent" icon tile @click="closeDialog">
              <v-icon size="24">mdi-close</v-icon>
            </v-btn>
          </v-toolbar>

          <div class="swap-entry-dialog-form-body">
            <v-container>
              <h5>
                Describe how {{ tenantTexts.swapEntryDialog.swap }} was realize.
              </h5>
            </v-container>
            <v-container class="swap-entry-dialog-card-container">
              <bh-text-area
                :counter="1000"
                :show-counter-in-range="10"
                label="Describe in detail (optional)"
                name="content"
                :value="newSwapEntryData.content"
                :readonly="readonly"
                :validate="true"
                :rules="[
                  value =>
                    (value || '').length <= 1000 || 'Max 1,000 characters',
                ]"
                @input-text="onInput({ name: 'content', value: $event })"
              />
            </v-container>
            <v-container>
              <h5>How do you assess the Category today?</h5>
            </v-container>
            <v-container>
              <v-row>
                <v-col>
                  <dialog-select
                    label="Difficulty Level"
                    name="difficultLevel"
                    :items="difficultLevelOptions"
                    @input="onInput"
                    :value="newSwapEntryData.difficultLevel"
                  />
                </v-col>
                <v-col>
                  <dialog-select
                    label="Impact Level"
                    name="impactLevel"
                    :items="impactLevelOptions"
                    @input="onInput"
                    :value="newSwapEntryData.impactLevel"
                  />
                </v-col>
              </v-row>
            </v-container>
            <v-container>
              <p class="accent--text mb-0">
                Latest Category evaluation as of
                {{ evaluationDate | moment('MMM DD, YYYY') }}:
              </p>
              <p>
                <strong class="category-name"
                  >{{ defaultDifficultLevel | lowerCased }} &
                  {{ defaultImpactLevel | lowerCased }} impact</strong
                >
              </p>
            </v-container>
            <v-container>
              <h5>Media (optional)</h5>
            </v-container>
            <v-container class="swap-entry-dialog-card-container pt-0">
              <thumbnail-picker
                label=""
                :current-file="currentFile"
                @thumbnail-picked="setThumbnail"
                :readonly="readonly"
              />
            </v-container>
            <v-container
              class="d-flex justify-space-between align-center swap-entry-dialog__tab--footer"
            >
              <v-spacer />
              <v-btn
                rounded
                color="primary"
                elevation="0"
                :loading="submitLoading"
                @click="onSubmit"
                :disabled="!formIsValidCheck"
              >
                Add Entry
              </v-btn>
            </v-container>
          </div>
        </div>
      </v-form>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import TenantTextsMixin from '@/mixins/tenantTexts'
import { debounce } from '@/helpers/debounce'
import DialogSelect from '@/components/forms/DialogSelect.vue'
import ThumbnailPicker from '@/components/inputs/ThumbnailPicker.vue'
import BhTextArea from '@/components/inputs/BhTextArea.vue'

const emptyForm = {
  content: '',
  impactLevel: 'MAJOR',
  difficultLevel: 'EASY',
  thumbnailUrl: null,
  mediaUrl: null,
  thumbnailValue: null,
}

export default {
  name: 'SwapEntryDialog',
  components: {
    DialogSelect,
    ThumbnailPicker,
    BhTextArea,
  },
  filters: {
    lowerCased: function(data = '') {
      return data.toLowerCase()
    },
  },
  mixins: [TenantTextsMixin],
  props: {
    showDialog: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    submitLoadingRefreshKey: {
      type: Number,
      default: 0,
    },
    defaultImpactLevel: {
      type: String,
      default: 'MAJOR',
    },
    defaultDifficultLevel: {
      type: String,
      default: 'EASY',
    },
    evaluationDate: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      dialog: false,
      submitLoading: false,
      newSwapEntryData: {
        content: '',
        impactLevel: 'MAJOR',
        difficultLevel: 'EASY',
        thumbnailUrl: null,
        mediaUrl: null,
        thumbnailValue: null,
      },
      repeatGoalValue: 1,
    }
  },
  watch: {
    showDialog: function(val) {
      if (val) {
        this.dialog = true
        this.newSwapEntryData.impactLevel = this.defaultImpactLevel
        this.newSwapEntryData.difficultLevel = this.defaultDifficultLevel
      } else {
        this.dialog = false
      }
    },
    submitLoadingRefreshKey: function(val) {
      this.submitLoading = false
    },
    defaultDifficultLevel: function(val) {
      this.newSwapEntryData.difficultLevel = val
    },
    defaultImpactLevel: function(val) {
      this.newSwapEntryData.impactLevel = val
    },
  },
  created() {},
  mounted() {
    this.newSwapEntryData.impactLevel = this.defaultImpactLevel
    this.newSwapEntryData.difficultLevel = this.defaultDifficultLevel
  },
  computed: {
    currentFile() {
      if (!this.newSwapEntryData?.thumbnailUrl) {
        return null
      }
      return {
        thumbnailUrl: this.newSwapEntryData?.thumbnailUrl,
        mediaUrl: this.newSwapEntryData?.mediaUrl || null,
      }
    },
    difficultLevelOptions() {
      return [
        {
          text: 'easy',
          value: 'EASY',
        },
        {
          text: 'hard',
          value: 'HARD',
        },
      ]
    },
    impactLevelOptions() {
      return [
        {
          text: 'major',
          value: 'MAJOR',
        },
        {
          text: 'minor',
          value: 'MINOR',
        },
      ]
    },
    formIsValidCheck() {
      return (
        ['EASY', 'HARD'].includes(this.newSwapEntryData?.difficultLevel) &&
        ['MINOR', 'MAJOR'].includes(this.newSwapEntryData?.impactLevel) &&
        (!this.newSwapEntryData?.content ||
          (this.newSwapEntryData.content &&
            this.newSwapEntryData.content.length <= 1000))
      )
    },
  },
  methods: {
    closeDialog() {
      this.$emit('close-swap-entry-dialog')
    },
    onClickOutside() {
      this.closeDialog()
    },
    onKeydown(key) {
      if (key.code === 'Escape' && key.isTrusted) this.closeDialog()
    },
    onInput({ name, value }) {
      this.newSwapEntryData[name] = value
    },
    setThumbnail(thumbnail = null) {
      if (thumbnail === null) {
        // remove
        this.newSwapEntryData.thumbnailValue = { remove: true }
      } else {
        const { type, file } = thumbnail

        if (type === 'media-library') {
          this.newSwapEntryData.thumbnailValue = { mlSourceResourceId: file.id }
        } else if (type === 'file-picker') {
          this.newSwapEntryData.thumbnailUrl = null
          this.newSwapEntryData.mediaUrl = null
          this.newSwapEntryData.thumbnailValue = file
        }
      }
    },
    onSubmit() {
      this.submitLoading = true
      this.$emit('on-submit', { ...this.newSwapEntryData })
    },
  },
}
</script>

<style lang="scss">
@import '@/styles/variables.scss';

.swap-entry-dialog {
  &__dialog {
    margin: $gutter;
    margin-left: auto;
    height: 100%;
    max-height: calc(100% - #{2 * $gutter}) !important;
    > .v-card {
      width: 100%;
      height: 100%;
      background-color: $bh-blue--4;
      overflow: auto;
    }
  }

  &__toolbar {
    flex-grow: 0;
  }

  &-toolbar-title {
    color: $bh-blue;
    font-size: 1.5715rem;
    font-weight: bold;
  }

  &-form {
    .category-name,
    .thumbnail-picker__header strong,
    .dialog-select-label,
    .bh-text-area__label {
      font-size: 14px;
      line-height: 20px;
      font-weight: 700;
      color: #6c7480 !important;
    }
  }
}
</style>
